// SMART TOOL MODAL — THE editing/inserting surface for every smart-object
// recipe (Smart Tools epic, owner directive 2026-07-23, verbatim: "For each
// tool, (including the ones we already have) I want the options to be
// presented in a perfectly styled modal instead of being opened in the left
// hand drawer. Each tool's modal needs to be specific to that tool, and allow
// one click styling by grade band, with all options for the tool intuitively
// laid out.").
//
// ONE component for ~all tools — per-tool "specific" layout stays DATA
// (ToolRecipe.modal: hero keys, columns, sectionOrder, hideAdvanced; see
// posterTools/types.ts) so hundreds of tools stay cheap. The schema-driven
// field machinery is LIFTED from the retired drawer edit mode, not forked:
// SmartObjectFieldControl / SmartObjectListField / SmartObjectFillField, the
// draft helpers in SmartObjectFieldUtils, and SmartObjectImportModal for the
// data-table's CSV/XLSX import.
//
// SURFACE (PaintPickerModal mechanics): a precise floating card —
// min(820px, 90vw) × min(560px, 84vh) — centered, draggable by its titlebar,
// deliberately NO backdrop scrim (the one-floating-surface law; the canvas
// stays visible beside the live preview). Esc rides the shared capture-phase
// stack (StylesExplorerModal): top surface only, so the paint modal (z 12000)
// or a nested import modal pops first. z 11000: above drawers/flyouts and the
// object-swap/styles modals, below the paint modal + eyedropper overlay.
//
// LAYOUT: LEFT = scrolling options pane (hero params first, then flat section
// groups with an auto-pairing 2-col field grid, advanced folded); RIGHT = LIVE
// PREVIEW pane — the recipe rendered at
// the current draft params (renderSmartObjectPreview, debounced 250 ms, subtle
// shimmer between renders, light/dark ground chip). TOP BAR: tool name +
// description tooltip + the GRADE-BAND one-click styling control
// ([K–2][3–5][6–8][9–12] → merge recipe.gradeBands[band] into the draft; the
// (bandId) => void API is deliberate — when the Theme system lands the control
// becomes a theme-variant selector invisibly). FOOTER: [Cancel][Insert] for
// mode 'insert', [Cancel][Apply changes] for mode 'edit'.
//
// UNDO LAWS: everything buffers in the DRAFT — Cancel/Esc closes with zero
// canvas writes. Insert = editor.addSmartObject(id, params, {gradeBand}) →
// ONE canvas.add → one history entry (and, through the insert-latched editor,
// it closes the left drawer if one was open — the insert latch survives the
// modal). Apply = editor.regenerateSelectedSmartObject(params, {target,
// gradeBand}) → the batched remove+add → one entry. The chosen band persists
// as SmartObjectData.gradeBand inside the same entry so reopening the modal
// lights the same chip.
//
// Opened cross-tree via smartToolModalController (paintModalController
// pattern): openSmartToolModal({recipeId, mode:'insert'|'edit'}). Entry
// points: ribbon "Edit <recipe>" button, canvas double-click on a smart
// object, the drawer's edit banner, and the Smart Tools browser (SM-B lane).
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useToast } from '../../../contexts/ToastContext';
import { getRecipe } from '../../../posterTools';
import { ensureFont } from './fonts/fontLoader';
import { renderSmartObjectPreview } from './smartObjectPreview';
import {
  contentApiAvailable,
  fetchStates,
  fetchSubjects,
  searchStandards,
  fetchStandardContent,
  customizeAvailable,
  customizeTool,
} from './smartContent';
import SmartObjectFieldControl from './SmartObjectFieldControl';
import SmartObjectListField from './SmartObjectListField';
import SmartObjectImportModal from './SmartObjectImportModal';
import { schemaSupportsTableImport } from './SmartObjectImportUtils';
import {
  buildParamsFromDraft,
  collectDraftFontFamilies,
  groupBySection,
  isFieldVisible,
  mergeBandPreset,
  seedDraft,
} from './SmartObjectFieldUtils';
import { registerSmartToolModalHost } from './smartToolModalController';
import { pushEscHandler, popEscHandler } from './inspector/text/StylesExplorerModal';
// The schema field controls carry cce-so-* classes — their styles live with
// the (list-mode-only) sidebar sheet; import it here too so the modal never
// depends on the drawer having mounted first.
import './SmartObjectsSidebar.css';
import './SmartToolModal.css';

// Segmented control faces. Ids are the GradeBandId contract (types.ts); the
// labels are the educator-facing faces. Chips without a recipe preset render
// disabled — presets are per-recipe AND per-band.
const GRADE_BANDS = [
  { id: 'K2', label: 'K–2' },
  { id: 'G35', label: '3–5' },
  { id: 'G68', label: '6–8' },
  { id: 'G912', label: '9–12' },
];

const PREVIEW_PX = 512; // square render edge; the pane downscales crisp
const PREVIEW_DEBOUNCE_MS = 250;
const MARGIN = 8; // keep the card this far inside the viewport while dragging

// Smart-fill enrichment poll (SM-J): a picked standard with no content yet makes
// the worker record demand (payload.enrichment === 'queued') and the product's
// graph fills it in within ~minutes. Poll the same fetch while the modal stays
// open — every 10s, up to 12 tries (~2 min) — then fall back to the muted
// nothing-yet state. Cleared on re-pick and unmount.
const FILL_POLL_MS = 10000;
const FILL_POLL_MAX = 12;

// Field types that must span a full row in the auto-pairing grid (rich/long
// controls); everything else (number/select/color/boolean/fill) is NARROW and
// pairs two-up. See renderField.
const WIDE_FIELD_TYPES = new Set(['text', 'multiline', 'font', 'image', 'list', 'json']);

/** Clamp a drag offset so the centered card stays on-screen (same law as
 *  PaintPickerModal — an axis bigger than the viewport stays centered). */
function clampOffset(delta, size, viewport) {
  const half = size / 2;
  const lo = MARGIN - viewport / 2 + half;
  const hi = viewport / 2 - MARGIN - half;
  if (lo > hi) return 0;
  return Math.max(lo, Math.min(hi, delta));
}

function SmartToolModal({ editor, config, onDismiss }) {
  const toast = useToast();
  const isEdit = config.mode === 'edit';

  // Session resolved ONCE at mount (the host remounts per open via the
  // controller's _id key): recipe + seed params + band + the live fabric
  // target (edit mode). The target OBJECT is captured because there is no
  // scrim — a canvas click can change the selection while the modal is up,
  // and Apply must regenerate the object the modal opened FOR.
  const [session] = useState(() => {
    if (isEdit) {
      const sel = editor?.getSelectedSmartObject?.();
      if (!sel?.recipe) return null;
      return {
        recipe: sel.recipe,
        initialParams: sel.data?.params || {},
        initialBand: typeof sel.data?.gradeBand === 'string' ? sel.data.gradeBand : null,
        target: editor?.selectedObjects?.[0] || null,
      };
    }
    const recipe = getRecipe(config.recipeId);
    if (!recipe) return null;
    return { recipe, initialParams: {}, initialBand: null, target: null };
  });
  const recipe = session?.recipe || null;

  const [draft, setDraft] = useState(() => (session ? seedDraft(session.recipe, session.initialParams) : {}));
  const [band, setBand] = useState(session?.initialBand ?? null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ground, setGround] = useState('light'); // preview backdrop
  const [preview, setPreview] = useState(null); // caller-owned object URL
  const [previewBusy, setPreviewBusy] = useState(true);
  const [previewStale, setPreviewStale] = useState(false);
  // Aspect-lock mirror (edit mode) — read once from the target, then owned
  // here so the checkbox never chases a changed canvas selection.
  const [aspectLocked, setAspectLocked] = useState(() => (
    session?.target ? session.target.data?.aspectLocked !== false : true
  ));

  // A config that can't resolve (no smart selection / unknown recipe) closes
  // itself with a toast — entry points normally pre-guard this.
  useEffect(() => {
    if (!session) {
      toast.showError(isEdit
        ? 'Select a smart object to edit its settings.'
        : 'That tool is not available in this build.');
      onDismiss?.();
    }
    // Mount-once by design (session is fixed for the life of the mount).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- live preview (debounced draft → renderSmartObjectPreview) ----
  // Monotonic seq guards out-of-order resolutions; URLs are caller-owned, so
  // every replaced/orphaned render revokes.
  const previewSeq = useRef(0);
  const previewUrlRef = useRef(null);
  useEffect(() => {
    if (!recipe) return undefined;
    const seq = ++previewSeq.current;
    setPreviewBusy(true);
    const timer = setTimeout(() => {
      let params;
      try {
        params = buildParamsFromDraft(recipe, draft);
      } catch (err) {
        // Half-typed input (empty number, short list): keep the last good
        // render and mark the preview paused instead of flashing errors.
        // The MESSAGE is surfaced (owner live-test: a bare "check the fields"
        // hid a validation failure from Smart-Fill content).
        if (previewSeq.current === seq) {
          setPreviewBusy(false);
          setPreviewStale(String(err?.message || 'check the fields'));
        }
        return;
      }
      renderSmartObjectPreview({ recipeId: recipe.id, params, size: PREVIEW_PX })
        .then((url) => {
          if (previewSeq.current !== seq) {
            if (url) URL.revokeObjectURL(url);
            return;
          }
          if (url) {
            if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = url;
            setPreview(url);
            setPreviewStale(false);
          } else {
            setPreviewStale('the tool could not draw these values');
          }
          setPreviewBusy(false);
        })
        .catch((err) => {
          if (previewSeq.current === seq) {
            setPreviewBusy(false);
            setPreviewStale(String(err?.message || 'the tool could not draw these values'));
          }
        });
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [recipe, draft]);
  useEffect(() => () => {
    previewSeq.current += 1; // orphan any in-flight render → it self-revokes
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  }, []);

  // Bounding-box resizes sync width/height into the object's params (engine
  // hook dispatches this event) — mirror JUST those two fields into the draft
  // (same law the drawer edit form had) so the size inputs read live values
  // without stomping other edits. Edit mode only; no scrim means the user CAN
  // resize the object while the modal is open.
  useEffect(() => {
    if (!isEdit) return undefined;
    const onSizeSynced = (e) => {
      const { width, height } = e.detail || {};
      setDraft((d) => {
        const next = { ...d };
        if (width !== undefined && 'width' in next) next.width = width;
        if (height !== undefined && 'height' in next) next.height = height;
        return next;
      });
    };
    window.addEventListener('cce-smart-size-synced', onSizeSynced);
    return () => window.removeEventListener('cce-smart-size-synced', onSizeSynced);
  }, [isEdit]);

  // Never edit a ghost (text3d-modal law): if the edit target leaves the
  // canvas (delete / undo replay) the modal closes. Our own Apply also fires
  // object:removed mid-swap — harmless, Apply closes the modal anyway.
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;
  useEffect(() => {
    const canvas = editor?.canvas;
    const target = session?.target;
    if (!isEdit || !canvas || !target) return undefined;
    const onRemoved = (opt) => {
      if (opt?.target === target) dismissRef.current?.();
    };
    canvas.on('object:removed', onRemoved);
    return () => canvas.off('object:removed', onRemoved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor?.canvas]);

  // ---- Esc (shared capture stack — top surface only) ----
  // Nested surfaces first: the import modal closes; the list-item modal and
  // an open FontPicker panel are swallowed (their own Done / click-away
  // closes them — the capture stack already ate the event).
  const cardRef = useRef(null);
  const escRef = useRef(null);
  escRef.current = () => {
    if (importOpen) {
      setImportOpen(false);
      return;
    }
    const card = cardRef.current;
    if (card && card.querySelector('.cce-modal-overlay, .cce-fp-panel')) return;
    onDismiss?.();
  };
  useEffect(() => {
    const handler = () => escRef.current?.();
    pushEscHandler(handler);
    return () => popEscHandler(handler);
  }, []);

  // ---- SMART FILL (SM-H): fill this tool from a curriculum standard ----
  // Renders only when the recipe declares a contentSource. The graph PROPOSES:
  // a pick fetches IIG content and merges recipe.contentSource.applyContent's
  // patch into the DRAFT (setDraft → live preview) — NOTHING touches the canvas
  // until Insert/Apply (teacher disposes). Every call is quiet-failing so the
  // standards service being down never blocks manual editing.
  const hasContentSource = !!recipe?.contentSource;
  const [fillOpen, setFillOpen] = useState(false);
  const [fillAvailable, setFillAvailable] = useState(null); // null = not yet probed
  const [fillStates, setFillStates] = useState([]);
  const [fillState, setFillState] = useState(() => {
    try { return JSON.parse(localStorage.getItem('pseSmartFill.v1') || '{}').state || ''; }
    catch { return ''; }
  });
  const [fillSubjects, setFillSubjects] = useState([]);
  const [fillSubject, setFillSubject] = useState('');
  const [fillQ, setFillQ] = useState('');
  const [fillResults, setFillResults] = useState([]);
  const [fillLoading, setFillLoading] = useState(false);
  const [fillBusy, setFillBusy] = useState(false);
  // { id, code, label, changed, generated, building, exhausted } — highlight + banner
  const [fillPicked, setFillPicked] = useState(null);
  const fillDebounce = useRef(null);
  const fillPollRef = useRef(null); // { token, tries, timer } — active enrichment poll
  // Fresh-draft mirror so the enrichment poll (runs across ~2 min) applies content
  // onto the CURRENT params, not the draft captured when the pick fired.
  const draftRef = useRef(draft);
  draftRef.current = draft;

  // ---- ✨ ADJUST WITH AI (lane IX): natural-language tool customization ----
  // A single-shot intent box that translates a teacher's plain-language request
  // into a SCHEMA-VALIDATED param patch (worker: POST /api/tools/customize), then
  // merges it into the DRAFT — live preview updates, nothing reaches the canvas
  // until Insert/Apply (model proposes, teacher disposes). Renders only when the
  // route is armed AND reachable (probe); the whole feature is invisible off.
  // Composes with Smart Fill: fill pulls graph CONTENT, intent adjusts VALUES.
  const [aiAvailable, setAiAvailable] = useState(null); // null = not yet probed
  const [aiOpen, setAiOpen] = useState(false);
  const [aiIntent, setAiIntent] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiNote, setAiNote] = useState(null);   // { text, changed } — violet/amber banner
  const [aiError, setAiError] = useState(null);  // soft error line
  useEffect(() => {
    let alive = true;
    customizeAvailable().then((ok) => { if (alive) setAiAvailable(ok); });
    return () => { alive = false; };
  }, []);

  // Availability + states — SELF-HEALING (owner live-test: a second modal
  // open could show a dead block after a transient blip; one-shot probing
  // turned seconds of outage into a bricked panel).
  //   • Warm the shared states cache on MOUNT (background, ignored errors).
  //   • While the block is expanded and not yet confirmed available, probe
  //     with automatic 4s retries (up to 5) before conceding (Try again).
  //   • If confirmed available but the states list is somehow EMPTY, re-fetch
  //     — covers a dropped/aborted first fetch without any user action.
  useEffect(() => {
    if (hasContentSource) fetchStates().catch(() => { /* warmup only */ });
  }, [hasContentSource]);
  useEffect(() => {
    if (!hasContentSource || !fillOpen) return undefined;
    if (fillAvailable === true && fillStates.length > 0) return undefined;
    if (fillAvailable === false) return undefined; // rests until Try again / re-expand
    let cancelled = false;
    let timer = null;
    let tries = 0;
    const attempt = () => {
      if (cancelled) return;
      tries += 1;
      contentApiAvailable().then((ok) => {
        if (cancelled) return;
        if (!ok) {
          if (tries >= 5) { setFillAvailable(false); return; }
          timer = setTimeout(attempt, 4000);
          return;
        }
        fetchStates()
          .then((rows) => {
            if (cancelled) return;
            if (rows.length) { setFillAvailable(true); setFillStates(rows); }
            else if (tries >= 5) setFillAvailable(false);
            else timer = setTimeout(attempt, 4000);
          })
          .catch(() => {
            if (cancelled) return;
            if (tries >= 5) setFillAvailable(false);
            else timer = setTimeout(attempt, 4000);
          });
      });
    };
    attempt();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [hasContentSource, fillOpen, fillAvailable, fillStates.length]);

  // Persist the last state pick (localStorage pseSmartFill.v1).
  useEffect(() => {
    if (!fillState) return;
    try { localStorage.setItem('pseSmartFill.v1', JSON.stringify({ state: fillState })); }
    catch { /* private mode / quota — non-fatal */ }
  }, [fillState]);

  // Subjects follow the chosen state — one silent 3s retry on failure (same
  // self-healing law as the states loader).
  useEffect(() => {
    if (!fillOpen || fillAvailable !== true || !fillState) { setFillSubjects([]); return undefined; }
    let cancelled = false;
    let timer = null;
    const load = (retryLeft) => {
      fetchSubjects(fillState)
        .then((rows) => {
          if (cancelled) return;
          if (rows.length || retryLeft <= 0) setFillSubjects(rows);
          else timer = setTimeout(() => load(retryLeft - 1), 3000);
        })
        .catch(() => {
          if (cancelled) return;
          if (retryLeft > 0) timer = setTimeout(() => load(retryLeft - 1), 3000);
          else setFillSubjects([]);
        });
    };
    load(2);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [fillOpen, fillAvailable, fillState]);

  // Debounced standard search (300ms). Empty/short q → don't fire (>=2 chars,
  // matching the worker's own text-search floor).
  useEffect(() => {
    if (!fillOpen || fillAvailable !== true || !fillState) { setFillResults([]); return undefined; }
    if (fillDebounce.current) clearTimeout(fillDebounce.current);
    const q = fillQ.trim();
    if (q.length < 2) { setFillResults([]); setFillLoading(false); return undefined; }
    setFillLoading(true);
    fillDebounce.current = setTimeout(() => {
      searchStandards({ state: fillState, subject: fillSubject || undefined, q, limit: 8 })
        .then((rows) => setFillResults(rows.slice(0, 8)))
        .catch(() => setFillResults([]))
        .finally(() => setFillLoading(false));
    }, 300);
    return () => { if (fillDebounce.current) clearTimeout(fillDebounce.current); };
  }, [fillOpen, fillAvailable, fillState, fillSubject, fillQ]);

  // Stop any enrichment poll when the modal unmounts (close/re-open remounts via
  // the host key). Touches only the stable ref, so a mount-once effect is safe.
  useEffect(() => () => {
    const p = fillPollRef.current;
    if (p?.timer) clearTimeout(p.timer);
    fillPollRef.current = null;
  }, []);

  // ---- drag (titlebar — PaintPickerModal mechanics) ----
  const [offset, setOffset] = useState({ dx: 0, dy: 0 });
  const dragRef = useRef(null);
  const onTitleDown = (e) => {
    if (e.button != null && e.button !== 0) return; // primary button only
    if (e.target.closest('button')) return; // band chips / ✕ stay clickable
    const rect = cardRef.current?.getBoundingClientRect();
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      baseDx: offset.dx,
      baseDy: offset.dy,
      w: rect?.width ?? 0,
      h: rect?.height ?? 0,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onTitleMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = clampOffset(d.baseDx + (e.clientX - d.startX), d.w, window.innerWidth);
    const dy = clampOffset(d.baseDy + (e.clientY - d.startY), d.h, window.innerHeight);
    setOffset({ dx, dy });
  };
  const onTitleUp = (e) => {
    const d = dragRef.current;
    if (d && e.currentTarget.releasePointerCapture) {
      try { e.currentTarget.releasePointerCapture(d.pointerId); } catch { /* ignore */ }
    }
    dragRef.current = null;
  };

  if (!session || !recipe) return null;

  // ---- band one-click styling ----
  // API stays (bandId) => void on purpose (Theme-system swap note, types.ts).
  const handleBand = (bandId) => {
    const preset = recipe.gradeBands?.[bandId];
    if (!preset) return;
    setDraft((d) => mergeBandPreset(recipe, d, preset));
    setBand(bandId);
  };
  const hasBands = !!recipe.gradeBands
    && GRADE_BANDS.some((b) => recipe.gradeBands[b.id]);

  // ---- smart-fill pick: fetch graph content → applyContent → MERGE into draft.
  // Draft-only (teacher disposes); quiet on failure so manual editing is never
  // blocked. draftRef.current is the LIVE param bag applyContent reads.
  const stopFillPoll = () => {
    const p = fillPollRef.current;
    if (p?.timer) clearTimeout(p.timer);
    fillPollRef.current = null;
  };

  // Apply a fetched payload onto the live draft. Returns the provenance the
  // banner needs, or { error:true } when the recipe's applyContent throws.
  const applyPayloadToDraft = (payload, row) => {
    let patch;
    try {
      patch = typeof recipe.contentSource.applyContent === 'function'
        ? recipe.contentSource.applyContent(payload, draftRef.current)
        : null;
    } catch (err) {
      return { error: true };
    }
    // Honesty check (owner 2026-07-23): a standard with no ready content returns
    // empty lists; applyContent leaves params untouched by design — the banner
    // then reads NOTHING CHANGED / BUILDING, never "Filled from".
    let changed = false;
    if (patch && typeof patch === 'object') {
      const base = draftRef.current;
      // Advanced (hidden) fields — the provenance stamps — must not count as a
      // visible fill: a patch that only touches them reads as NOTHING CHANGED
      // (amber), never as a confident "Filled/AI-drafted" banner.
      const hiddenKeys = new Set(
        (recipe.schema || []).filter((f) => f.advanced).map((f) => f.key),
      );
      const visiblyChanged = (k) => {
        try { return JSON.stringify(patch[k]) !== JSON.stringify(base[k]); } catch { return true; }
      };
      changed = Object.keys(patch).some((k) => !hiddenKeys.has(k) && visiblyChanged(k));
      const anyChange = changed || Object.keys(patch).some(visiblyChanged);
      if (anyChange) setDraft((d) => ({ ...d, ...patch }));
    }
    // Some sub-standards carry no code — fall back to a description snippet so
    // the banner never reads "Filled from —".
    const label = row.code || `“${(row.description || '').slice(0, 44).trim()}…”`;
    return {
      changed,
      label,
      generated: payload?.generated === true,
      enrichment: payload?.enrichment,
      source: payload?.source,
    };
  };

  // While the worker is enriching this standard (payload.enrichment==='queued'),
  // poll the same fetch until content lands. Stops on first content, exhaustion,
  // re-pick, and unmount. No paid work here — the product's graph does the draft.
  const startFillPoll = (row, label) => {
    stopFillPoll();
    const token = `${row.id}:${Date.now()}`;
    const state = { token, tries: 0, timer: null };
    fillPollRef.current = state;
    const isCurrent = () => fillPollRef.current === state;
    const settleExhausted = () => {
      stopFillPoll();
      setFillPicked((p) => (p && p.id === row.id ? { ...p, building: false, exhausted: true } : p));
    };
    const tick = () => {
      if (!isCurrent()) return;
      state.tries += 1;
      fetchStandardContent(row.id, recipe.contentSource?.wants)
        .then((payload) => {
          if (!isCurrent()) return;
          const res = applyPayloadToDraft(payload, row);
          if (res.error) { stopFillPoll(); return; }
          if (res.changed) {
            stopFillPoll();
            setFillPicked({ id: row.id, code: row.code, label, changed: true, generated: res.generated, source: res.source, building: false });
          } else if (state.tries >= FILL_POLL_MAX || res.enrichment !== 'queued') {
            settleExhausted();
          } else {
            state.timer = setTimeout(tick, FILL_POLL_MS);
          }
        })
        .catch(() => {
          if (!isCurrent()) return;
          if (state.tries >= FILL_POLL_MAX) settleExhausted();
          else state.timer = setTimeout(tick, FILL_POLL_MS);
        });
    };
    state.timer = setTimeout(tick, FILL_POLL_MS);
  };

  const handlePickStandard = async (row) => {
    if (fillBusy || !recipe.contentSource) return;
    stopFillPoll(); // cancel any poll left from a previous pick
    setFillBusy(true);
    try {
      // The recipe's wants ride along so the worker can draft exactly the
      // buckets this TOOL needs when the graph lacks them.
      const payload = await fetchStandardContent(row.id, recipe.contentSource?.wants);
      const res = applyPayloadToDraft(payload, row);
      if (res.error) { toast.showError('Could not apply that standard to this tool.'); return; }
      if (res.changed) {
        // generated ⇒ AI-drafted (violet); else filled-from-library (green).
        // source 'instant-draft' sharpens the violet copy ("just now").
        setFillPicked({ id: row.id, code: row.code, label: res.label, changed: true, generated: res.generated, source: res.source, building: false });
      } else if (res.enrichment === 'queued') {
        // No content yet, but the worker recorded demand — "building" + poll.
        setFillPicked({ id: row.id, code: row.code, label: res.label, changed: false, generated: false, building: true });
        startFillPoll(row, res.label);
      } else {
        // 'unavailable' / absent — nothing ready and none coming (amber).
        setFillPicked({ id: row.id, code: row.code, label: res.label, changed: false, generated: false, building: false });
      }
    } catch (err) {
      // unavailable / 404 — user-initiated, so a soft nudge; never a hard break.
      toast.showError?.("Couldn't reach the standards service — fill this in manually for now.");
    } finally {
      setFillBusy(false);
    }
  };

  // ---- ✨ Adjust with AI: build the editable-field list the worker constrains
  // the model to (from recipe.schema — EXCLUDE advanced provenance fields), send
  // current draft params + the intent, merge the validated patch into the draft.
  const buildCustomizeFields = () => (recipe.schema || [])
    .filter((f) => f && f.key && !f.advanced)
    .map((f) => {
      const out = { key: f.key, type: f.type, label: f.label };
      if (Array.isArray(f.options)) out.options = f.options.map((o) => ({ value: o.value, label: o.label }));
      if (typeof f.min === 'number') out.min = f.min;
      if (typeof f.max === 'number') out.max = f.max;
      if (typeof f.step === 'number') out.step = f.step;
      return out;
    });

  const handleAiSend = async () => {
    const intent = aiIntent.trim();
    if (!intent || aiBusy) return;
    setAiBusy(true);
    setAiError(null);
    try {
      // draftRef.current is the LIVE param bag (styling the teacher already set
      // is preserved — the worker prunes it to scalar field values for context).
      const { patch, note } = await customizeTool({
        toolId: recipe.id,
        fields: buildCustomizeFields(),
        params: draftRef.current,
        intent,
      });
      const keys = patch && typeof patch === 'object' ? Object.keys(patch) : [];
      if (keys.length) {
        setDraft((d) => ({ ...d, ...patch })); // draft-only → live preview updates
        setAiNote({ text: note || `Updated ${keys.length} setting${keys.length === 1 ? '' : 's'}.`, changed: true });
      } else {
        // Nothing mapped in-schema — the worker's honest fallback note, amber.
        setAiNote({ text: note || "I couldn't map that to this tool's settings — try rephrasing.", changed: false });
      }
    } catch (err) {
      // unavailable / timeout — user-initiated, so a soft line, never a hard break.
      setAiError("Couldn't reach the AI helper — try again, or adjust the settings by hand.");
    } finally {
      setAiBusy(false);
    }
  };

  // ---- field layout (ToolRecipe.modal spec — DATA, no per-tool React) ----
  const modalSpec = recipe.modal || {};
  const heroKeys = Array.isArray(modalSpec.hero) ? modalSpec.hero : [];
  const heroSet = new Set(heroKeys);
  // Conditional field visibility (visibleWhen, SM-F2): evaluate every field
  // against the LIVE draft before it reaches the layout, so a controller flip
  // (e.g. element → "Custom", or "auto category color" off) shows/hides its
  // dependents in place. Hidden fields keep their draft values — they are only
  // withheld from render; the draft (and thus generate) is never mutated.
  const heroFields = heroKeys
    .map((k) => recipe.schema.find((f) => f.key === k))
    .filter(Boolean)
    .filter((f) => isFieldVisible(f, draft));
  const mainFields = recipe.schema.filter(
    (f) => !f.advanced && !heroSet.has(f.key) && isFieldVisible(f, draft),
  );
  const advancedFields = modalSpec.hideAdvanced
    ? []
    : recipe.schema.filter(
        (f) => f.advanced && !heroSet.has(f.key) && isFieldVisible(f, draft),
      );
  const { ungrouped: ungroupedFields, groups: sectionGroups } =
    groupBySection(mainFields, modalSpec.sectionOrder);

  const setField = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  // Auto-pairing (density pass, 2026-07-23): section bodies are a 2-col grid.
  // NARROW controls (number/select/color/boolean/fill) take one column and
  // pair two-up; WIDE ones (text/multiline/font/image/list/json) span the row.
  // On a narrow-heavy tool (the data-table Colors section is the stress case)
  // this halves the vertical run; an all-wide tool reads as a single column.
  // A recipe's modal.columns:2 is now simply the default behavior.
  const renderField = (field, hero = false) => {
    const wide = WIDE_FIELD_TYPES.has(field.type);
    return (
    <div
      key={field.key}
      className={`cce-so-field${wide ? ' cce-so-field-wide' : ' cce-so-field-narrow'}${hero ? ' cce-stm-herofield' : ''}`}
    >
      <label className="cce-so-label">{field.label}</label>
      {field.type === 'list' ? (
        <SmartObjectListField
          field={field}
          value={draft[field.key]}
          onChange={(v) => setField(field.key, v)}
        />
      ) : (
        <SmartObjectFieldControl
          field={field}
          value={draft[field.key]}
          onChange={(v) => setField(field.key, v)}
        />
      )}
      {field.help && <p className="cce-so-help">{field.help}</p>}
    </div>
    );
  };

  // ---- data-table import (schema has BOTH columns + rows list fields) ----
  const canImportTable = schemaSupportsTableImport(recipe.schema);
  const columnsField = canImportTable ? recipe.schema.find((f) => f.key === 'columns') : null;
  const rowsField = canImportTable ? recipe.schema.find((f) => f.key === 'rows') : null;
  const commitLabel = isEdit ? 'Apply changes' : 'Insert';
  const handleImportConfirm = ({ columns, rows }) => {
    setDraft((d) => ({ ...d, columns, rows }));
    setImportOpen(false);
    toast.showSuccess?.(
      `Imported ${columns.length} column${columns.length === 1 ? '' : 's'} and ${rows.length} row${rows.length === 1 ? '' : 's'} — press ${commitLabel} to update the table.`,
    );
  };

  // ---- aspect lock (edit mode; object behavior, not a recipe param) ----
  const handleAspect = (locked) => {
    setAspectLocked(locked);
    const canvas = editor?.canvas;
    const target = session.target;
    if (canvas && target && canvas.getActiveObject?.() !== target) {
      // No scrim — a canvas click may have moved the selection. The verb acts
      // on the active object, so re-activate the modal's target first.
      try { canvas.setActiveObject(target); } catch { /* removed mid-flight */ }
    }
    editor?.setSmartAspectLock?.(locked);
  };

  // ---- commit paths (one-entry laws — see header) ----
  const ensureDraftFonts = async () => {
    const families = collectDraftFontFamilies(recipe.schema, draft);
    if (families.length > 0) {
      // Soft-fail like the rest of the font system: canvas falls back.
      await Promise.allSettled(families.map((f) => ensureFont(f)));
    }
  };

  const handleInsert = async () => {
    if (!editor || busy) return;
    let params;
    try {
      params = buildParamsFromDraft(recipe, draft);
    } catch (err) {
      toast.showError(err.message);
      return;
    }
    setBusy(true);
    try {
      await ensureDraftFonts();
      // ONE canvas.add → one history entry; params ride generateSmartObject
      // directly (no insert-then-regenerate needed). Through the latched
      // editor this also closes the left drawer if it was open.
      await editor.addSmartObject(recipe.id, params, band ? { gradeBand: band } : undefined);
      onDismiss?.();
    } catch (err) {
      console.error('Smart object insert failed:', err);
      toast.showError(`Could not add "${recipe.name}". ${err.message || 'Please try again.'}`);
      setBusy(false);
    }
  };

  const handleApply = async () => {
    if (!editor || busy) return;
    let params;
    try {
      params = buildParamsFromDraft(recipe, draft);
    } catch (err) {
      toast.showError(err.message);
      return;
    }
    setBusy(true);
    try {
      await ensureDraftFonts();
      // Batched remove+add → one entry. target pins the object the modal
      // opened for (the selection may have moved — no scrim).
      await editor.regenerateSelectedSmartObject(params, {
        target: session.target || undefined,
        gradeBand: band || undefined,
      });
      onDismiss?.();
    } catch (err) {
      console.error('Smart object update failed:', err);
      toast.showError(`Could not update "${recipe.name}". ${err.message || 'Please try again.'}`);
      setBusy(false);
    }
  };

  return createPortal(
    <div
      className="cce-stm"
      role="dialog"
      aria-label={`${recipe.name} options`}
      data-smart-tool-modal={recipe.id}
      data-mode={config.mode}
      ref={cardRef}
      style={{ transform: `translate(-50%, -50%) translate(${offset.dx}px, ${offset.dy}px)` }}
    >
      <div
        className="cce-stm-titlebar"
        onPointerDown={onTitleDown}
        onPointerMove={onTitleMove}
        onPointerUp={onTitleUp}
        onPointerCancel={onTitleUp}
      >
        <div className="cce-stm-title">
          <span className="cce-stm-toolname">{recipe.name}</span>
          <span
            className="cce-stm-info"
            title={recipe.description}
            aria-label="About this tool"
            role="img"
          >
            ⓘ
          </span>
        </div>

        {hasBands && (
          <div className="cce-stm-bands" role="group" aria-label="One-click styling by grade band">
            <span className="cce-stm-bands-label">Style for</span>
            <div className="cce-stm-bands-seg">
              {GRADE_BANDS.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  className={`cce-stm-band${band === b.id ? ' active' : ''}`}
                  disabled={!recipe.gradeBands?.[b.id]}
                  aria-pressed={band === b.id}
                  title={`One-click styling for grades ${b.label}`}
                  onClick={() => handleBand(b.id)}
                >
                  {b.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <button
          type="button"
          className="cce-stm-close"
          aria-label="Close"
          title="Close without applying"
          onClick={() => onDismiss?.()}
        >
          ×
        </button>
      </div>

      <div className="cce-stm-body">
        <div className="cce-stm-options">
          {recipe.contentSource && (
            <div className="cce-stm-fill" data-open={fillOpen ? 'true' : 'false'}>
              <button
                type="button"
                className="cce-stm-fill-toggle"
                aria-expanded={fillOpen}
                onClick={() => {
                  // A failed probe must not stick for the modal's lifetime
                  // (owner: states wouldn't load after a service blip) — every
                  // expand from the failed state re-probes.
                  if (!fillOpen && fillAvailable === false) setFillAvailable(null);
                  setFillOpen((o) => !o);
                }}
              >
                <span className="cce-stm-fill-spark" aria-hidden="true">✦</span>
                <span className="cce-stm-fill-toggle-label">Fill from a standard</span>
                <span className="cce-stm-fill-chev" aria-hidden="true">{fillOpen ? '▾' : '▸'}</span>
              </button>

              {fillOpen && (
                <div className="cce-stm-fill-panel">
                  <div className="cce-stm-fill-heading">Smart fill</div>
                  {fillAvailable === false ? (
                    <p className="cce-stm-fill-muted">
                      Standards service isn&rsquo;t reachable right now — you can fill everything in manually.{' '}
                      <button
                        type="button"
                        className="cce-stm-fill-retry"
                        onClick={() => setFillAvailable(null)}
                      >
                        Try again
                      </button>
                    </p>
                  ) : (
                    <>
                      <div className="cce-stm-fill-row">
                        <select
                          className="cce-stm-fill-control"
                          aria-label="State"
                          value={fillState}
                          onChange={(e) => { setFillState(e.target.value); setFillSubject(''); setFillResults([]); }}
                        >
                          <option value="">State…</option>
                          {fillStates.map((s) => (
                            <option key={s.code} value={s.code}>{s.code} — {s.name}</option>
                          ))}
                        </select>
                        <select
                          className="cce-stm-fill-control"
                          aria-label="Subject"
                          value={fillSubject}
                          disabled={!fillState || fillSubjects.length === 0}
                          onChange={(e) => setFillSubject(e.target.value)}
                        >
                          <option value="">All subjects</option>
                          {fillSubjects.map((s) => (
                            <option key={s.subject} value={s.subject}>{s.subject}</option>
                          ))}
                        </select>
                      </div>
                      <input
                        className="cce-stm-fill-control cce-stm-fill-search"
                        type="text"
                        placeholder={fillState ? 'Search standards…' : 'Pick a state first'}
                        value={fillQ}
                        disabled={!fillState}
                        onChange={(e) => setFillQ(e.target.value)}
                      />
                      {fillLoading && <div className="cce-stm-fill-note">Searching…</div>}
                      {!fillLoading && fillResults.length > 0 && (
                        <ul className="cce-stm-fill-results">
                          {fillResults.map((row) => (
                            <li key={row.id}>
                              <button
                                type="button"
                                className={`cce-stm-fill-result${fillPicked?.id === row.id ? ' picked' : ''}`}
                                disabled={fillBusy}
                                onClick={() => handlePickStandard(row)}
                              >
                                <code className="cce-stm-fill-code">{row.code}</code>
                                <span className="cce-stm-fill-desc">{(row.description || '').slice(0, 90)}</span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                      {!fillLoading && fillState && fillQ.trim().length >= 2 && fillResults.length === 0 && (
                        <div className="cce-stm-fill-note">No matches — try a full word (&ldquo;fractions&rdquo;) or a standard code (&ldquo;4.NF&rdquo;).</div>
                      )}
                      {fillPicked && fillPicked.building && (
                        <div className="cce-stm-fill-confirm cce-stm-fill-working">
                          <span>
                            Building content for <strong>{fillPicked.label}</strong> now — usually under a
                            couple of minutes. It will fill in automatically when ready.
                          </span>
                        </div>
                      )}
                      {fillPicked && fillPicked.changed && fillPicked.generated && (
                        <div className="cce-stm-fill-confirm cce-stm-fill-ai">
                          {fillPicked.source === 'instant-draft' ? (
                            <>
                              AI-drafted just now for <strong>{fillPicked.label}</strong> — not yet
                              teacher-reviewed. Check it carefully before printing.
                            </>
                          ) : (
                            <>
                              AI-drafted for <strong>{fillPicked.label}</strong> — not yet teacher-reviewed.
                              Check it carefully before printing.
                            </>
                          )}
                        </div>
                      )}
                      {fillPicked && fillPicked.changed && !fillPicked.generated && (
                        <div className="cce-stm-fill-confirm">
                          Filled from <strong>{fillPicked.label}</strong> — review and edit anything.
                        </div>
                      )}
                      {fillPicked && !fillPicked.changed && !fillPicked.building && (
                        <div className="cce-stm-fill-confirm cce-stm-fill-empty">
                          {fillPicked.exhausted ? (
                            <>
                              Still building <strong>{fillPicked.label}</strong> — this one&rsquo;s taking
                              longer than usual. Reopen this tool in a few minutes and it&rsquo;ll fill in.
                            </>
                          ) : (
                            <>
                              <strong>{fillPicked.label}</strong> doesn&rsquo;t have ready-made content for
                              this tool yet — nothing was changed. Try another standard, or fill it in yourself.
                            </>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {aiAvailable === true && (
            <div className="cce-stm-ai" data-open={aiOpen ? 'true' : 'false'}>
              <button
                type="button"
                className="cce-stm-ai-toggle"
                aria-expanded={aiOpen}
                onClick={() => { setAiOpen((o) => !o); }}
              >
                <span className="cce-stm-ai-spark" aria-hidden="true">✨</span>
                <span className="cce-stm-ai-toggle-label">Adjust with AI</span>
                <span className="cce-stm-ai-chev" aria-hidden="true">{aiOpen ? '▾' : '▸'}</span>
              </button>

              {aiOpen && (
                <div className="cce-stm-ai-panel">
                  <div className="cce-stm-ai-heading">Describe a change</div>
                  <textarea
                    className="cce-stm-ai-input"
                    rows={2}
                    placeholder="e.g. make it 4 columns and use the ticket style"
                    value={aiIntent}
                    disabled={aiBusy}
                    onChange={(e) => setAiIntent(e.target.value)}
                    onKeyDown={(e) => {
                      // Cmd/Ctrl+Enter sends; a bare Enter keeps typing (multiline intent).
                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleAiSend(); }
                    }}
                  />
                  <div className="cce-stm-ai-actions">
                    <span className="cce-stm-ai-hint">Adjusts this tool&rsquo;s settings — review before printing.</span>
                    <button
                      type="button"
                      className="cce-stm-ai-send"
                      disabled={aiBusy || !aiIntent.trim()}
                      onClick={handleAiSend}
                    >
                      {aiBusy ? 'Thinking…' : 'Send'}
                    </button>
                  </div>
                  {aiError && <div className="cce-stm-ai-line cce-stm-ai-err">{aiError}</div>}
                  {!aiError && aiNote && (
                    <div className={`cce-stm-ai-line${aiNote.changed ? ' cce-stm-ai-ok' : ' cce-stm-ai-none'}`}>
                      {aiNote.changed ? `✨ ${aiNote.text} — review before printing.` : aiNote.text}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {canImportTable && (
            <button
              type="button"
              className="cce-so-import-btn"
              onClick={() => setImportOpen(true)}
            >
              ⇪ Import data…
              <span className="cce-so-import-btn-sub">Paste from a spreadsheet, or load a .csv / .xlsx</span>
            </button>
          )}

          {heroFields.length > 0 && (
            <div className="cce-stm-hero">
              {heroFields.map((f) => renderField(f, true))}
            </div>
          )}

          {ungroupedFields.length > 0 && (
            <div className="cce-stm-card">
              <div className="cce-stm-card-body">
                {ungroupedFields.map((f) => renderField(f))}
              </div>
            </div>
          )}

          {sectionGroups.map((group) => (
            <div key={group.section} className="cce-stm-card">
              <div className="cce-stm-card-heading">{group.section}</div>
              <div className="cce-stm-card-body">
                {group.fields.map((f) => renderField(f))}
              </div>
            </div>
          ))}

          {advancedFields.length > 0 && (
            <div className="cce-stm-card cce-stm-advanced">
              <button
                type="button"
                className="cce-so-advanced-toggle"
                aria-expanded={advancedOpen}
                onClick={() => setAdvancedOpen((o) => !o)}
              >
                <span className="cce-so-chevron">{advancedOpen ? '▾' : '▸'}</span>
                Advanced options
                <span className="cce-so-count">{advancedFields.length}</span>
              </button>
              {advancedOpen && (
                <div className="cce-stm-card-body">
                  {advancedFields.map((f) => renderField(f))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className={`cce-stm-preview ground-${ground}`}>
          <div className="cce-stm-stage">
            {preview ? (
              <img className="cce-stm-stage-img" src={preview} alt="" draggable={false} />
            ) : (
              !previewBusy && <span className="cce-stm-stage-ph" aria-hidden="true">▦</span>
            )}
            {previewBusy && <div className="cce-stm-shimmer" aria-hidden="true" />}
          </div>
          <div className="cce-stm-preview-bar">
            <span className={`cce-stm-preview-note${previewStale ? ' stale' : ''}`}>
              {previewStale
                ? `Preview paused — ${typeof previewStale === 'string' ? previewStale : 'check the fields'}`
                : 'Live preview'}
            </span>
            <button
              type="button"
              className="cce-stm-ground-chip"
              title="Flip the preview background"
              onClick={() => setGround((g) => (g === 'light' ? 'dark' : 'light'))}
            >
              {ground === 'light' ? '◐ Dark' : '◑ Light'}
            </button>
          </div>
          {isEdit && (
            <label className="cce-so-checkbox-row cce-stm-aspect">
              <input
                type="checkbox"
                checked={aspectLocked}
                onChange={(e) => handleAspect(e.target.checked)}
              />
              <span>Lock proportions when resizing</span>
            </label>
          )}
        </div>
      </div>

      <div className="cce-stm-footer">
        <button
          type="button"
          className="cce-stm-btn"
          disabled={busy}
          onClick={() => onDismiss?.()}
        >
          Cancel
        </button>
        <button
          type="button"
          className="cce-stm-btn primary"
          disabled={busy}
          onClick={isEdit ? handleApply : handleInsert}
        >
          {busy ? (isEdit ? 'Applying…' : 'Adding…') : commitLabel}
        </button>
      </div>

      {importOpen && canImportTable && (
        <SmartObjectImportModal
          maxCols={Number.isFinite(columnsField?.maxItems) ? columnsField.maxItems : Infinity}
          maxRows={Number.isFinite(rowsField?.maxItems) ? rowsField.maxItems : Infinity}
          onConfirm={handleImportConfirm}
          onClose={() => setImportOpen(false)}
        />
      )}
    </div>,
    document.body,
  );
}

/** The singleton host — mount ONCE (StudioCoreEditor, beside the other edit
 *  modals) with the INSERT-LATCHED editor facade, so an Insert from the modal
 *  closes the left drawer exactly like a drawer-tile insert. Keyed by the
 *  controller's per-open id so opening a new target remounts (re-centers,
 *  re-seeds the draft). */
export function SmartToolModalHost({ editor }) {
  const [config, setConfig] = useState(null);
  useEffect(() => registerSmartToolModalHost(setConfig), []);
  if (!config) return null;
  return (
    <SmartToolModal
      key={config._id}
      editor={editor}
      config={config}
      onDismiss={() => setConfig(null)}
    />
  );
}

export default SmartToolModalHost;
